import { useQuery } from "@tanstack/react-query";
import {
  Users, Layers, ShoppingBag, Award, GitBranch, Bell, Radio, FileText,
} from "lucide-react";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

import { AdminAPI } from "@/lib/admin-api";
import { formatCurrency, formatNumber } from "@/lib/format";
import { Skeleton } from "@/components/ui/Skeleton";
import { MetricCard } from "./components/MetricCard";
import { ChartCard } from "./components/ChartCard";

const COLORS = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#94a3b8", "#8b5cf6"];

export function AdminDashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: AdminAPI.dashboard,
    refetchInterval: 15_000,
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }

  const entryStatusData = Object.entries(data.entries.by_status).map(([k, v]) => ({
    name: k.replace(/_/g, " "),
    value: v,
  }));

  const notifData = [
    { name: "Queued",    value: data.notifications.queued    },
    { name: "Delivered", value: data.notifications.delivered },
    { name: "Failed",    value: data.notifications.failed    },
    { name: "DLQ",       value: data.notifications.dead_letter },
  ];

  const outboxData = [
    { name: "Pending",    value: data.outbox.pending },
    { name: "Dispatched", value: data.outbox.dispatched },
    { name: "DLQ",        value: data.outbox.dead_letter },
  ];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Admin Dashboard</h1>
        <p className="text-sm text-surface-500 mt-1">
          Real-time system metrics · auto-refreshes every 15 seconds
        </p>
      </header>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <MetricCard icon={Users}      label="Users (active/total)" tone="info"
                    value={`${formatNumber(data.users.active)} / ${formatNumber(data.users.total)}`}
                    hint="Registered members" />
        <MetricCard icon={Layers}     label="Club Entries" tone="warning"
                    value={formatNumber(data.entries.total)}
                    hint={`${Object.keys(data.entries.by_status).length} status states`} />
        <MetricCard icon={Award}      label="Rewards Paid" tone="success"
                    value={formatCurrency(data.completions.reward_grand_total)}
                    hint={`${formatNumber(data.completions.total)} completions`} />
        <MetricCard icon={ShoppingBag} label="Ledger Entries" tone="neutral"
                    value={formatNumber(data.ledger.entries)}
                    hint="Immutable financial records" />

        <MetricCard icon={GitBranch}  label="Placement Waiting" tone="warning"
                    value={formatNumber(data.placement_queue.waiting)}
                    hint={data.placement_queue.failed > 0
                      ? `${data.placement_queue.failed} failed — review needed`
                      : "Queue healthy"} />
        <MetricCard icon={Bell}       label="Notif. Delivered" tone="success"
                    value={formatNumber(data.notifications.delivered)}
                    hint={data.notifications.dead_letter > 0
                      ? `${data.notifications.dead_letter} DLQ`
                      : "All delivered"} />
        <MetricCard icon={Radio}      label="Outbox Pending" tone={data.outbox.pending > 100 ? "danger" : "info"}
                    value={formatNumber(data.outbox.pending)}
                    hint={`${formatNumber(data.outbox.dispatched)} dispatched`} />
        <MetricCard icon={FileText}   label="Outbox DLQ" tone={data.outbox.dead_letter > 0 ? "danger" : "neutral"}
                    value={formatNumber(data.outbox.dead_letter)}
                    hint={data.outbox.dead_letter > 0 ? "Requires intervention" : "Healthy"} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <ChartCard title="Entries by Status" subtitle="Distribution across lifecycle">
          {entryStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={entryStatusData} dataKey="value" nameKey="name"
                     cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                  {entryStatusData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-72 flex items-center justify-center text-sm text-surface-500">
              No entries yet
            </div>
          )}
        </ChartCard>

        <ChartCard title="Notification Pipeline" subtitle="Current delivery states">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={notifData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Outbox Health" subtitle="Event dispatch state">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={outboxData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Bar dataKey="value" fill="#22c55e" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
